import { createContext, ReactNode, useContext, useMemo, useState } from "react";

// MUI
import { GridFilterModel } from "@mui/x-data-grid-premium";
// Types
import { IDeal, IMyCIT } from "@/Common/types/MyCit/model";
import { IGetCITMaxValuesResponse } from "@DataPages/api/financeManagement/cit/queries";

interface IMyCitContext extends Omit<IMyCitContextProviderProps, "children" | "administrationToolsDmsSources"> {
	filteredDeals: string[] | null;
	setFilteredDeals: (value: string[] | null) => void;
}

const MyCitContext = createContext<IMyCitContext>({
	deals: [],
	filteredDeals: null,
	setFilteredDeals: () => {},
	filterModel: { items: [] },
	setFilterModel: () => {},
});

interface IMyCitContextProviderProps {
	children: ReactNode;
	myCit?: IMyCIT;
	deals: IDeal[];
	citSettings?: IGetCITMaxValuesResponse;
	filterModel: GridFilterModel;
	setFilterModel: React.Dispatch<React.SetStateAction<GridFilterModel>>;
}

const MyCitContextProvider = ({
	children,
	myCit,
	deals,
	citSettings,
	filterModel,
	setFilterModel,
}: Readonly<IMyCitContextProviderProps>) => {
	const [filteredDeals, setFilteredDeals] = useState<string[] | null>(null);

	const value = useMemo(
		() => ({
			myCit,
			deals,
			filteredDeals,
			setFilteredDeals,
			citSettings,
			filterModel,
			setFilterModel,
		}),
		[myCit, deals, filteredDeals, setFilteredDeals, citSettings, filterModel, setFilterModel]
	);

	return <MyCitContext.Provider value={value}>{children}</MyCitContext.Provider>;
};

function useMyCitContextData() {
	const context = useContext(MyCitContext);
	if (!context) {
		throw new Error("useMyCitContextData must be used within a MyCitContextProvider");
	}
	return context;
}

export { MyCitContextProvider, useMyCitContextData };
